import { createAsyncThunk, createSlice, } from "@reduxjs/toolkit";
import { getStats } from "../../api/myClub/getStats";
import { computeNewPlayerRecs as computeNewPlayerRecsApi, getUserRecommendations, markRecommendationsAsViewed, } from "../../api/myClub/recommendations";
import { buildBestClubSquad, } from "../../api/userSquads/buildBestClubSquad";
import { getActiveSquad, } from "../../api/userSquads/getActiveSquad";
const initialState = {
    clubStats: {
        clubName: "",
        clubBadgeId: 0,
        lastImportedAt: 0,
        coins: 0,
        clubValue: 0,
        sbcFodder: 0,
        sbcFodderCalculation: {},
        potentialCoins: 0,
        transferList: 0,
        rank: 0,
        playersCount: 0,
        evolvedPlayersCount: 0,
        squadRecsLastUpdatedAt: 0,
        squadChangedSinceLastRecs: false,
        activeSquad: {
            name: "",
            formation: "f442",
            players: [],
            manager: {},
        },
    },
    fetchClubStatsStatus: "idle",
    activeSquad: undefined,
    fetchActiveSquadStatus: "idle",
    bestSquad: undefined,
    buildBestSquadStatus: "idle",
    bestSquadOpened: false,
    showingBestSquad: false,
    recommendations: [],
    fetchRecommendationStatus: "idle",
    computeRecommendationStatus: "idle",
};
export const fetchClubStats = createAsyncThunk("my-club/fetch-club-stats", async () => getStats());
export const fetchActiveSquad = createAsyncThunk("my-club/fetch-active-squad", async () => getActiveSquad());
export const buildBestSquad = createAsyncThunk("my-club/build-best-squad", async () => {
    const squads = await buildBestClubSquad();
    if (!squads.length)
        throw new Error("No squads found");
    return squads[0];
});
export const fetchRecommendations = createAsyncThunk("my-club/fetch-recommendations", async () => {
    // Signed in by now: the list itself needs an account to answer.
    const recommendations = await getUserRecommendations();
    if (recommendations.some((r) => r.isNew))
        void markRecommendationsAsViewed().catch(() => { });
    return recommendations;
});
/** New recommendations for the active squad. Metered: gate it first. */
export const computeNewPlayerRecs = createAsyncThunk("my-club/compute-new-player-recs", async () => computeNewPlayerRecsApi());
const myClubSlice = createSlice({
    name: "myClub",
    initialState,
    reducers: {
        /**
         * Back to "no club known". The stats belong to the account, not the
         * device: `lastImportedAt` is what the club-import gate reads, so leaving
         * the previous user's would wave the next one straight past it.
         */
        resetClub: () => initialState,
        markBestSquadOpened: (state) => {
            state.bestSquadOpened = true;
        },
        /** Puts the best squad on the pitch, or the active squad back. */
        setShowingBestSquad: (state, action) => {
            state.showingBestSquad = action.payload;
        },
        /**
         * Puts the best squad away after a failed build. The status stays
         * "rejected": web's onboarding builds in the background only from "idle",
         * so a failure isn't retried on its own, again and again.
         */
        resetBestSquad: (state) => {
            state.showingBestSquad = false;
            state.bestSquad = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClubStats.pending, (state) => {
            state.fetchClubStatsStatus = "pending";
        })
            .addCase(fetchClubStats.fulfilled, (state, action) => {
            state.clubStats = action.payload;
            state.fetchClubStatsStatus = "fulfilled";
        })
            .addCase(fetchClubStats.rejected, (state) => {
            state.fetchClubStatsStatus = "rejected";
        })
            .addCase(fetchActiveSquad.pending, (state) => {
            state.fetchActiveSquadStatus = "pending";
        })
            .addCase(fetchActiveSquad.fulfilled, (state, action) => {
            state.activeSquad = action.payload;
            state.fetchActiveSquadStatus = "fulfilled";
        })
            .addCase(fetchActiveSquad.rejected, (state) => {
            state.fetchActiveSquadStatus = "rejected";
        })
            .addCase(buildBestSquad.pending, (state) => {
            state.buildBestSquadStatus = "pending";
        })
            .addCase(buildBestSquad.fulfilled, (state, action) => {
            state.bestSquad = action.payload;
            state.buildBestSquadStatus = "fulfilled";
        })
            .addCase(buildBestSquad.rejected, (state) => {
            state.buildBestSquadStatus = "rejected";
        })
            .addCase(fetchRecommendations.pending, (state) => {
            state.fetchRecommendationStatus = "pending";
        })
            .addCase(fetchRecommendations.fulfilled, (state, action) => {
            state.recommendations = action.payload;
            state.fetchRecommendationStatus = "fulfilled";
        })
            .addCase(fetchRecommendations.rejected, (state) => {
            state.fetchRecommendationStatus = "rejected";
        })
            .addCase(computeNewPlayerRecs.pending, (state) => {
            state.computeRecommendationStatus = "pending";
        })
            .addCase(computeNewPlayerRecs.fulfilled, (state, action) => {
            state.recommendations = action.payload;
            state.computeRecommendationStatus = "fulfilled";
            state.clubStats.squadRecsLastUpdatedAt = Date.now();
            state.clubStats.squadChangedSinceLastRecs = false;
        })
            .addCase(computeNewPlayerRecs.rejected, (state) => {
            state.computeRecommendationStatus = "rejected";
        });
    },
});
export const { resetClub, resetBestSquad, markBestSquadOpened, setShowingBestSquad, } = myClubSlice.actions;
export const myClubSelector = (state) => state.myClubReducer;
export const clubStatsSelector = (state) => state.myClubReducer.clubStats;
export const fetchClubStatsStatusSelector = (state) => state.myClubReducer.fetchClubStatsStatus;
export const myClubActiveSquadSelector = (state) => state.myClubReducer.activeSquad;
export const fetchActiveSquadStatusSelector = (state) => state.myClubReducer.fetchActiveSquadStatus;
export const bestSquadSelector = (state) => state.myClubReducer.bestSquad;
export const buildBestSquadStatusSelector = (state) => state.myClubReducer.buildBestSquadStatus;
export const showingBestSquadSelector = (state) => state.myClubReducer.showingBestSquad;
export const bestSquadOpenedSelector = (state) => state.myClubReducer.bestSquadOpened;
export const recommendationsSelector = (state) => state.myClubReducer.recommendations;
export const myClubReducer = myClubSlice.reducer;
export default myClubSlice.reducer;
//# sourceMappingURL=myClubSlice.js.map